import {
  createState,
  State,
  StateMethods,
  useState as useHookState,
} from '@hookstate/core';
import { IAssetLog } from '@models/AssetLog.model';
import { IAuth0User } from '@models/Auth0User.model';
import { IContact } from '@models/Contact.model';
import { ICustomField } from '@models/CustomField.model';
import { IDocument } from '@models/Document.model';
import { IEquipmentWithoutProduct } from '@models/Equipment.model';
import { IFieldConfiguration } from '@models/FieldConfiguration.model';
import { IPicture } from '@models/Picture.model';
import { IResource } from '@models/Resource.model';
import { indexCustomField } from '@services/queries/customField';

import {
  getEquipment,
  getEquipmentContacts,
  getEquipmentDocuments,
  getEquipmentResources,
  getEquipmentStatusAuthor,
} from '@services/queries/equipments';
import { getAssetHistory } from '@services/queries/equipments/getAssetHistory/getAssetHistory';
import { indexFieldConfiguration } from '@services/queries/fieldsConfiguration';
import { getPicturesByEquipment } from '@services/queries/pictures/getPicturesByEquipment/getPicturesByEquipment';
import { getUserMembers } from '@services/queries/studios';
import { IUserMember } from '@services/queries/studios/getUserMembers/getUserMembers';
import { getProductValue } from '@utils/getProductValue';
import {
  createContext,
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useQuery, useQueryClient, UseQueryResult } from 'react-query';
import { useParams } from 'react-router-dom';
import { AssetRaw } from './Asset.raw';
import {
  AddTagCategoryPageProps,
  AddTagPageProps,
} from './components/AddTag/AddTag';
import {
  ProductSpecsKey,
  productSpecsQuery,
} from './components/Sidebar/components/Data/components/ProductSpec/ProductSpecs.query';
import { ShortcutRefs } from './components/Sidebar/components/Shortcuts/Shortcuts.utils';

export interface IAssetContext {
  equipmentQuery?: UseQueryResult<IEquipmentWithoutProduct, unknown>;
  removeTags: StateMethods<boolean>;
  configQuery?: UseQueryResult<IFieldConfiguration[], unknown>;
  picturesQuery?: UseQueryResult<IPicture[], unknown>;
  customFieldQuery?: UseQueryResult<ICustomField[], unknown>;
  documentsQuery?: UseQueryResult<IDocument[], unknown>;
  contactsQuery?: UseQueryResult<IContact[], unknown>;
  resourcesQuery?: UseQueryResult<IResource[], unknown>;
  statusAuthor?: UseQueryResult<IAuth0User | undefined, unknown>;
  assetHistory?: UseQueryResult<IAssetLog[], unknown>;
  membersQuery?: UseQueryResult<IUserMember[], unknown>;
  openTagModal: State<boolean>;
  openTagInfo: State<boolean>;
  addTagPage: State<AddTagPageProps>;
  addTagCategoryPage: State<AddTagCategoryPageProps>;
  openAddDocument: State<boolean>;
  openEditDocument: State<boolean>;
  openEditResource: State<boolean>;
  openAddContact: State<boolean>;
  openAddResource: State<boolean>;
  openEditContact: State<boolean>;
  showResources: State<boolean>;
  showAdd: boolean;
  show: boolean;
  sentNewComment: boolean;
  selectedPicture: string;
  isEditingField: string;
  setShowAdd: Dispatch<SetStateAction<boolean>>;
  setShow: Dispatch<SetStateAction<boolean>>;
  setSentNewComment: Dispatch<SetStateAction<boolean>>;
  setSelectedPicture: Dispatch<SetStateAction<string>>;
  setIsEditingField: Dispatch<SetStateAction<string>>;
  selectedDocument: State<IDocument | null>;
  selectedResource: State<IResource | null>;
  sortDocumentByFavorite: State<boolean>;
  sortContactByFavorite: State<boolean>;
  sortResourceByFavorite: State<boolean>;
  selectedContactId: State<string | null>;
  reOrderPinnedData: State<boolean>;
  unpinPinnedData: State<boolean>;
  restorePinnedData: State<boolean>;
  sidebarHeadingHeight: State<number>;
  showAllTags: State<boolean>;

  shortcutRefs: React.MutableRefObject<ShortcutRefs>;
}
export const specDataUpdatedAt = createState('');

export const AssetContext = createContext<IAssetContext>({
  removeTags: createState<boolean>(false),
  openTagModal: createState<boolean>(false),
  openTagInfo: createState<boolean>(false),
  addTagPage: createState<AddTagPageProps>('add_tag'),
  addTagCategoryPage: createState<AddTagCategoryPageProps>('unset'),
  openAddDocument: createState<boolean>(false),
  openEditDocument: createState<boolean>(false),
  openEditResource: createState<boolean>(false),
  openAddContact: createState<boolean>(false),
  openAddResource: createState<boolean>(false),
  openEditContact: createState<boolean>(false),
  sortDocumentByFavorite: createState<boolean>(true),
  sortContactByFavorite: createState<boolean>(true),
  sortResourceByFavorite: createState<boolean>(true),
  selectedDocument: createState<IDocument | null>(null),
  selectedResource: createState<IResource | null>(null),
  selectedContactId: createState<string | null>(null),
  showResources: createState<boolean>(false),
  reOrderPinnedData: createState<boolean>(false),
  unpinPinnedData: createState<boolean>(false),
  restorePinnedData: createState<boolean>(false),
  sidebarHeadingHeight: createState<number>(24),
  showAllTags: createState<boolean>(false),
  showAdd: false,
  show: false,
  sentNewComment: false,
  selectedPicture: '',
  isEditingField: '',
  setShowAdd: (() => {}) as any,
  setShow: (() => {}) as any,
  setSentNewComment: (() => {}) as any,
  setSelectedPicture: (() => {}) as any,
  setIsEditingField: (() => {}) as any,
  shortcutRefs: { current: {} },
});

export const AssetQuery = () => {
  const params = useParams<{ equipmentId: string }>();

  const [showAdd, setShowAdd] = useState<boolean>(false);
  const [show, setShow] = useState<boolean>(false);
  const [sentNewComment, setSentNewComment] = useState(false);
  const [selectedPicture, setSelectedPicture] = useState<string>('');
  const [isEditingField, setIsEditingField] = useState<string>('');

  const removeTags = useHookState<boolean>(false);
  const openTagModal = useHookState<boolean>(false);
  const openTagInfo = useHookState<boolean>(false);
  const openAddDocument = useHookState<boolean>(false);
  const openEditDocument = useHookState<boolean>(false);
  const openEditResource = useHookState<boolean>(false);
  const openAddContact = useHookState<boolean>(false);
  const openAddResource = useHookState<boolean>(false);
  const openEditContact = useHookState<boolean>(false);
  const sortDocumentByFavorite = useHookState<boolean>(true);
  const sortContactByFavorite = useHookState<boolean>(true);
  const sortResourceByFavorite = useHookState<boolean>(true);
  const showResources = useHookState<boolean>(false);
  const reOrderPinnedData = useHookState<boolean>(false);
  const unpinPinnedData = useHookState<boolean>(false);
  const restorePinnedData = useHookState<boolean>(false);
  const addTagPage = useHookState<AddTagPageProps>('add_tag');
  const addTagCategoryPage = useHookState<AddTagCategoryPageProps>('unset');
  const selectedDocument = useHookState<IDocument | null>(null);
  const selectedResource = useHookState<IResource | null>(null);
  const selectedContactId = useHookState<string | null>(null);
  const sidebarHeadingHeight = useHookState<number>(24);
  const showAllTags = useHookState<boolean>(false);
  const shortcutRefs = useRef<ShortcutRefs>({});

  const equipmentQuery = useQuery(['equipments', params.equipmentId], () =>
    getEquipment(params.equipmentId),
  );

  const configQuery = useQuery(['fieldsConfiguration', 'productHeader'], () =>
    indexFieldConfiguration('productHeader'),
  );

  const picturesQuery = useQuery(
    ['equipments', params.equipmentId, 'pictures'],
    () => getPicturesByEquipment({ id: params.equipmentId }),
  );

  const documentsQuery = useQuery(
    ['equipments', params.equipmentId, 'documents'],
    () => getEquipmentDocuments({ equipmentId: params.equipmentId }),
  );

  const contactsQuery = useQuery(
    ['equipments', params.equipmentId, 'contacts'],
    () => getEquipmentContacts({ equipmentId: params.equipmentId }),
  );

  const resourcesQuery = useQuery(
    ['equipments', params.equipmentId, 'resources'],
    () => getEquipmentResources({ equipmentId: params.equipmentId }),
  );

  const statusAuthor = useQuery(
    ['equipments', params.equipmentId, 'statusAuthor'],
    () => getEquipmentStatusAuthor({ equipmentId: params.equipmentId }),
  );

  const assetHistory = useQuery(
    ['equipments', params.equipmentId, 'assetHistory'],
    () => getAssetHistory({ equipmentId: params.equipmentId }),
  );

  const customFieldQuery = useQuery('customFields', indexCustomField);

  const membersQuery = useQuery('members', getUserMembers);
  const queryClient = useQueryClient();

  const { productId } = getProductValue(equipmentQuery.data);
  async function prefetch() {
    // Prefetch product Specs
    const qKey = productSpecsQuery.queryKey as ProductSpecsKey;
    const q1 = queryClient.prefetchQuery({
      ...productSpecsQuery,
      queryKey: [qKey[0], qKey[1], productId, '', specDataUpdatedAt.get()],
    });
    const queries = [q1];

    await Promise.all(queries);
  }

  useEffect(() => {
    prefetch();
  }, [productId]);
  return (
    <AssetContext.Provider
      value={{
        equipmentQuery,
        configQuery,
        removeTags,
        addTagCategoryPage,
        addTagPage,
        openTagModal,
        openTagInfo,
        picturesQuery,
        isEditingField,
        setIsEditingField,
        selectedPicture,
        setSelectedPicture,
        setShow,
        setShowAdd,
        show,
        showAdd,
        customFieldQuery,
        openAddDocument,
        openEditDocument,
        openEditResource,
        openAddContact,
        openAddResource,
        selectedDocument,
        documentsQuery,
        sortDocumentByFavorite,
        contactsQuery,
        selectedContactId,
        openEditContact,
        sortContactByFavorite,
        resourcesQuery,
        showResources,
        sortResourceByFavorite,
        selectedResource,
        reOrderPinnedData,
        unpinPinnedData,
        restorePinnedData,
        statusAuthor,
        assetHistory,
        membersQuery,
        sidebarHeadingHeight,
        showAllTags,
        shortcutRefs,
        sentNewComment,
        setSentNewComment,
      }}
    >
      <AssetRaw />
    </AssetContext.Provider>
  );
};
